import {
  identify as amplitudeIdentify,
  Identify,
  reset as amplitudeReset,
  revenue as amplitudeRevenue,
  Revenue,
  setGroup as amplitudeSetGroup,
  setOptOut as amplitudeSetOptOut,
  setUserId,
  track,
} from "@amplitude/analytics-browser";
import posthog from "posthog-js";
import { useEffect, useState } from "react";

import { getExperimentClient } from "src/lib/experimentClient";

/**
 * Unified instrumentation utilities for analytics and event tracking.
 *
 * IMPORTANT: This module assumes that the following services have been initialized:
 * - Amplitude Analytics: initialized in App.tsx via amplitude.init()
 * - PostHog: initialized in index.tsx via PostHogProvider
 * - Amplitude Experiment: initialized in App.tsx (for A/B testing)
 *
 * All tracking functions send events to BOTH Amplitude and PostHog simultaneously.
 */

// ============================================================================
// TYPES
// ============================================================================

type EventProperties = Record<string, string | number | boolean | undefined>;

interface IdentifyData {
  userId: string;
  traits?: {
    email?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    [key: string]: string | number | boolean | undefined;
  };
}

interface RevenueData {
  productId: string;
  price: number;
  quantity?: number;
  revenueType?: string;
  eventProperties?: EventProperties;
}

// Feature flag types
export type ExperimentVariant = "control" | "test";

export type ExperimentVariantState = {
  variant: ExperimentVariant;
  /** True only after the enabled flag has resolved to an assignment. */
  ready: boolean;
};

/**
 * How long `useExperimentVariantState` waits for a real flag assignment before
 * falling back to `control`.
 *
 * A blocked or failed /flags request is indistinguishable from a deleted flag —
 * posthog-js reports both as an absent key, fires its callbacks regardless, and
 * never retries. Without a bound, callers that treat "not ready" as "do
 * nothing" would be stuck for the whole session, and for two of them that is a
 * dead end rather than a no-op (see the note in the hook).
 */
const FLAG_RESOLUTION_FALLBACK_MS = 3000;

// Add new feature flag keys here as experiments are created (e.g. "my-experiment")
export type FeatureFlagKey =
  | "week-zero-v2"
  | "welcome-back-card"
  | "dot-booking-gate-lesson-7"
  | "dot-booking-gate-transit-funds"
  | "course-video-2x-speed"
  | "lesson-player-progress-footer"
  | "job-search-kickoff-after-unit-1"
  | "application-result-referral-ask";

// ============================================================================
// CORE TRACKING FUNCTIONS
// ============================================================================

/**
 * Track an event to both Amplitude and PostHog.
 */
export function trackEvent(
  eventName: string,
  eventProperties: EventProperties = {}
): void {
  track(eventName, eventProperties);
  posthog.capture(eventName, eventProperties);
}

/**
 * Track a page view to both Amplitude and PostHog.
 */
export function trackPage(
  category: string,
  pageName: string,
  properties: EventProperties = {}
): void {
  const eventName = `Page View - ${category} - ${pageName}`;
  track(eventName, { category, page: pageName, ...properties });
  posthog.capture("$pageview", {
    $current_url: window.location.href,
    category,
    page: pageName,
    ...properties,
  });
}

// ============================================================================
// IDENTITY MANAGEMENT
// ============================================================================

/**
 * Identify a user in both Amplitude and PostHog.
 */
export function identify({ userId, traits = {} }: IdentifyData): void {
  // Amplitude identify - set user ID and user properties
  setUserId(userId);
  const identifyObj = new Identify();
  Object.entries(traits).forEach(([key, value]) => {
    if (value !== undefined) {
      identifyObj.set(key, String(value));
    }
  });
  amplitudeIdentify(identifyObj);

  // PostHog identify
  posthog.identify(userId, traits);

  // Make identify traits available for feature-flag evaluation immediately.
  // `posthog.identify` sends traits as `$set`, but those only reach the flag
  // evaluation endpoint after server-side ingestion — so a cohort-targeted
  // flag read early in the same session can resolve before `cohortId` lands
  // on the profile and fall through to the default (e.g. control). Registering
  // the traits as flag-eval overrides sends them with the flags request right
  // away and reloads flags, which re-fires onFeatureFlags subscribers with the
  // correct assignment. See useExperimentVariant.
  const flagProperties = Object.fromEntries(
    Object.entries(traits).filter(([, value]) => value !== undefined)
  );
  if (Object.keys(flagProperties).length > 0) {
    posthog.setPersonPropertiesForFlags(flagProperties);
  }
}

/**
 * Reset user identity in both Amplitude and PostHog.
 * Call this on logout to clear the current user's data and start fresh.
 * CRITICAL for multi-user devices to prevent data leakage between users.
 */
export function resetIdentity(): void {
  amplitudeReset();
  posthog.reset();
}

// ============================================================================
// GROUP ANALYTICS
// ============================================================================

/**
 * Associate a user with a group (e.g., organization, team, company).
 * Useful for B2B analytics to track events at the organization level.
 *
 * @param groupType - The type of group (e.g., "organization", "team", "company")
 * @param groupName - The name/ID of the group (can be a single value or array for multiple groups)
 *
 * @example
 * setGroup("organization", "acme-corp");
 * setGroup("team", ["engineering", "product"]);
 */
export function setGroup(
  groupType: string,
  groupName: string | string[]
): void {
  // Amplitude: setGroup sets the group for all future events
  amplitudeSetGroup(groupType, groupName);

  // PostHog: group() associates the user with a group
  // PostHog expects a single group name, so we use the first one if array
  const primaryGroupName = Array.isArray(groupName) ? groupName[0] : groupName;
  posthog.group(groupType, primaryGroupName);
}

// ============================================================================
// REVENUE TRACKING
// ============================================================================

/**
 * Track a revenue event to both Amplitude and PostHog.
 * Use this for purchases, subscriptions, or any monetary transactions.
 *
 * @example
 * trackRevenue({
 *   productId: "premium-subscription",
 *   price: 29.99,
 *   quantity: 1,
 *   revenueType: "subscription",
 * });
 */
export function trackRevenue({
  productId,
  price,
  quantity = 1,
  revenueType,
  eventProperties = {},
}: RevenueData): void {
  // Amplitude: Use Revenue object for proper revenue tracking
  const revenueObj = new Revenue()
    .setProductId(productId)
    .setPrice(price)
    .setQuantity(quantity);

  if (revenueType) {
    revenueObj.setRevenueType(revenueType);
  }

  amplitudeRevenue(revenueObj);

  // PostHog: Track as a custom event with revenue properties
  posthog.capture("Purchase", {
    $set: {
      last_purchase_date: new Date().toISOString(),
    },
    productId,
    price,
    quantity,
    revenue: price * quantity,
    revenueType,
    currency: "USD",
    ...eventProperties,
  });
}

// ============================================================================
// SUPER PROPERTIES
// ============================================================================

/**
 * Set persistent properties that will be sent with ALL future events.
 * Useful for setting context like app version, user tier, or feature flags.
 *
 * @example
 * setSuperProperties({
 *   appVersion: "2.1.0",
 *   userTier: "premium",
 *   platform: "web",
 * });
 */
export function setSuperProperties(properties: EventProperties): void {
  // Amplitude: Use Identify to set user properties
  const identifyObj = new Identify();
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined) {
      identifyObj.set(key, String(value));
    }
  });
  amplitudeIdentify(identifyObj);

  // PostHog: Use register() for super properties
  posthog.register(properties);
}

// ============================================================================
// PRIVACY & OPT-OUT
// ============================================================================

/**
 * Enable or disable all tracking for GDPR/privacy compliance.
 * When opted out, no events will be sent to Amplitude or PostHog.
 *
 * @param optOut - true to stop tracking, false to resume tracking
 */
export function setOptOut(optOut: boolean): void {
  amplitudeSetOptOut(optOut);

  if (optOut) {
    posthog.opt_out_capturing();
  } else {
    posthog.opt_in_capturing();
  }
}

// ============================================================================
// FEATURE FLAGS & EXPERIMENTS
// ============================================================================

/**
 * Sync an experiment variant to Amplitude and PostHog for analytics correlation.
 * Sets the variant as a user property in both tools so events can be segmented
 * by experiment group regardless of which tool you're querying.
 */
function syncVariantToAnalytics(
  flagKey: string,
  variant: ExperimentVariant
): void {
  const propertyKey = `experiment_${flagKey}`;

  const identifyObj = new Identify();
  identifyObj.set(propertyKey, variant);
  amplitudeIdentify(identifyObj);

  posthog.setPersonProperties({ [propertyKey]: variant });

  // Also set in Amplitude Experiment if available
  const experimentClient = getExperimentClient();
  if (experimentClient) {
    experimentClient.exposure(flagKey);
  }
}

export interface UseExperimentVariantOptions {
  flagKey: FeatureFlagKey;
  urlParamOverride?: boolean;
  /**
   * The PostHog flag value that means "test cohort". PostHog multivariate
   * flags use arbitrary variant keys, so pass this experiment's test-variant
   * key (e.g. "study-guide-variant"). Defaults to "test".
   */
  testValue?: string;
  /**
   * Whether the user is actually eligible for this experiment on this render.
   * When false, the hook returns "control" without firing exposure or writing
   * the `experiment_*` user property — keeping exposure tied to eligibility
   * rather than to component mount. Defaults to true for backwards compat,
   * but callers gating on a resource/route condition should pass it.
   */
  enabled?: boolean;
}

/**
 * Hook to get the experiment variant and its readiness for a feature flag.
 * PostHog is the source of truth for variant assignment.
 * Amplitude receives the variant as a user property for analytics correlation.
 *
 * Starts unready and resolves when PostHog's onFeatureFlags fires. Callers that
 * branch on the assignment must hold their behavior until `ready` is true.
 *
 * @param options.flagKey - The feature flag key to check
 * @param options.urlParamOverride - Allow ?variant=test or ?variant=control for local testing
 * @param options.enabled - Gate exposure on eligibility (default true)
 * @returns The experiment variant plus whether the assignment has resolved
 *
 * @example
 * const { variant, ready } = useExperimentVariantState({ flagKey: "my-experiment" });
 * if (!ready) return <Loading />;
 * if (variant === "test") return <NewSignupFlow />;
 * return <OldSignupFlow />;
 */
export function useExperimentVariantState({
  flagKey,
  urlParamOverride = false,
  testValue = "test",
  enabled = true,
}: UseExperimentVariantOptions): ExperimentVariantState {
  const [state, setState] = useState<ExperimentVariantState>({
    variant: "control",
    ready: false,
  });

  useEffect(() => {
    // Not eligible on this render — return control without polluting exposure
    // or the `experiment_*` user property. Lets callers mount the hook
    // unconditionally (Rules of Hooks) while gating exposure to the cohort.
    if (!enabled) {
      setState({ variant: "control", ready: false });
      return undefined;
    }

    // Check URL override first (for local testing only). Synchronous, so it
    // resolves immediately and never depends on PostHog having loaded.
    if (urlParamOverride) {
      const urlVariant = new URLSearchParams(window.location.search).get(
        "variant"
      );
      if (urlVariant === "test" || urlVariant === "control") {
        setState({ variant: urlVariant, ready: true });
        syncVariantToAnalytics(flagKey, urlVariant);
        // No PostHog subscription to tear down on this path.
        return undefined;
      }
    }

    // Local dev: skip PostHog and default to the test variant so the
    // experiment is visible without a key or URL gymnastics. `?variant=test`
    // (above) still works in any env.
    if (process.env.NODE_ENV === "development") {
      setState({ variant: "test", ready: true });
      syncVariantToAnalytics(flagKey, "test");
      return undefined;
    }

    // PostHog loads feature flags asynchronously, after a network round trip.
    // Reading getFeatureFlag() once on mount races that request: if it has
    // not resolved yet the flag reads as undefined and the variant is stuck
    // on "control" forever. Subscribe instead — onFeatureFlags fires once
    // flags are available (and immediately if they already are), so the
    // variant re-resolves when the real assignment arrives. `testValue` is
    // the multivariate flag's test-variant key.
    // `undefined` from getFeatureFlag means "this key is not in the flag set we
    // hold", which is NOT the same as "this user is in the control arm" —
    // that's `false`. onFeatureFlags fires immediately on subscribe whenever
    // PostHog has already hit /flags, so the first fire can carry an empty set
    // (after resetIdentity, which clears the cache while the app navigates on
    // without a reload) and resolving that to "control" hands callers a
    // confident wrong answer.
    //
    // But absent is ALSO what a blocked or failed /flags request looks like:
    // posthog-js fires its callbacks anyway, sets `decideEndpointWasHit`, and
    // never retries — so simply waiting for a better answer waits forever. Not
    // every caller can sit in "pending": `useResourceNavigation` stops
    // advancing the lesson, and the permit / DOT gate screens render a skeleton
    // with no release path. So hold briefly for a real assignment, then fall
    // back to control — which is what all of these did before this hook learned
    // the difference.
    let settled = false;
    let lastSynced: ExperimentVariant | undefined;
    const resolve = (resolved: ExperimentVariant) => {
      settled = true;
      setState({ variant: resolved, ready: true });
      // Only on a CHANGE. `syncVariantToAnalytics` calls
      // `setPersonProperties`, which reloads flags, which re-fires this
      // callback — re-syncing an unchanged variant is a self-sustaining
      // /flags + $set loop that eats the client's event rate limit.
      if (lastSynced === resolved) return;
      lastSynced = resolved;
      syncVariantToAnalytics(flagKey, resolved);
    };

    const unsubscribe = posthog.onFeatureFlags(() => {
      const raw = posthog.getFeatureFlag(flagKey);
      if (raw === undefined) return;
      resolve(raw === testValue ? "test" : "control");
    });

    const fallback = window.setTimeout(() => {
      if (!settled) resolve("control");
    }, FLAG_RESOLUTION_FALLBACK_MS);

    return () => {
      window.clearTimeout(fallback);
      unsubscribe();
    };
  }, [flagKey, urlParamOverride, testValue, enabled]);

  return state;
}

/**
 * Backwards-compatible variant-only view. Consumers whose behavior must wait
 * for PostHog (for example a navigation gate) should use
 * `useExperimentVariantState` and honor its `ready` field.
 */
export function useExperimentVariant(
  options: UseExperimentVariantOptions
): ExperimentVariant {
  return useExperimentVariantState(options).variant;
}

/**
 * Get a feature flag value without using a hook (for non-component code).
 *
 * @param flagKey - The feature flag key to check
 * @returns The flag value (boolean, string, or undefined if not set)
 */
export function getFeatureFlag(
  flagKey: FeatureFlagKey
): boolean | string | undefined {
  return posthog.getFeatureFlag(flagKey);
}

/**
 * Flags that may be turned on via `?flags=<key>` on production URLs.
 * Add a flag here only when stakeholder/support previews actually need to
 * bypass PostHog rollout — every other gated experience should keep the
 * default rollout discipline.
 */
const URL_FLAG_OVERRIDE_ALLOWLIST: ReadonlySet<FeatureFlagKey> =
  new Set<FeatureFlagKey>();

/**
 * Read `?flags=foo,bar` from the URL. In dev, every flag passes through; in
 * prod we restrict to the allowlist above so a leaked preview URL can't opt
 * a visitor into unrelated unreleased features.
 */
function readFlagOverrides(): string[] {
  if (typeof window === "undefined") return [];
  const param = new URLSearchParams(window.location.search).get("flags");
  if (!param) return [];
  const requested = param
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (process.env.NODE_ENV === "development") return requested;
  return requested.filter((key): key is FeatureFlagKey =>
    URL_FLAG_OVERRIDE_ALLOWLIST.has(key as FeatureFlagKey)
  );
}

/**
 * Check if a feature is enabled (boolean convenience function).
 *
 * Honors `?flags=<key>` URL params (comma-separated) for keys in
 * `URL_FLAG_OVERRIDE_ALLOWLIST` (or any flag in dev), so a preview URL can
 * opt the visitor into a gated experience.
 *
 * @param flagKey - The feature flag key to check
 * @returns true if the feature is enabled, false otherwise
 */
export function isFeatureEnabled(flagKey: FeatureFlagKey): boolean {
  if (readFlagOverrides().includes(flagKey)) return true;
  return posthog.isFeatureEnabled(flagKey) ?? false;
}

/**
 * Subscribe to a boolean PostHog feature flag (a rollout gate, not an
 * experiment — no exposure event, no `experiment_*` user property).
 *
 * Mirrors `useExperimentVariant`'s loading behavior: starts `false` and
 * re-resolves when PostHog's `onFeatureFlags` fires (a one-time read on
 * mount would race the async flag fetch and stick on `false`). Honors
 * `?flags=<key>` URL overrides (any flag in dev, allowlist in prod) and
 * defaults to ON in local development so gated UI is visible without a
 * PostHog key.
 *
 * @example
 * const weekZeroV2 = useFeatureFlag("week-zero-v2");
 * if (weekZeroV2) return <NewCard />;
 * return <LegacyCard />;
 */
export function useFeatureFlag(flagKey: FeatureFlagKey): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // URL override — synchronous, never depends on PostHog having loaded.
    if (readFlagOverrides().includes(flagKey)) {
      setEnabled(true);
      return undefined;
    }

    // Local dev: skip PostHog so the gated experience is visible without a
    // key (same convention as useExperimentVariant's dev default).
    if (process.env.NODE_ENV === "development") {
      setEnabled(true);
      return undefined;
    }

    const unsubscribe = posthog.onFeatureFlags(() => {
      setEnabled(posthog.isFeatureEnabled(flagKey) ?? false);
    });

    return unsubscribe;
  }, [flagKey]);

  return enabled;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook that returns all instrumentation functions.
 * Use this when you prefer hook-style access to tracking functions.
 *
 * @example
 * const { trackEvent, trackPage, identify, resetIdentity } = useInstrumentation();
 * trackEvent("Button Clicked", { buttonId: "submit" });
 */
export function useInstrumentation() {
  return {
    // Core tracking
    trackEvent,
    trackPage,
    // Identity
    identify,
    resetIdentity,
    // Group analytics
    setGroup,
    // Revenue
    trackRevenue,
    // Super properties
    setSuperProperties,
    // Privacy
    setOptOut,
    // Feature flags
    getFeatureFlag,
    isFeatureEnabled,
  };
}
